import React, { ComponentType, useCallback, useEffect } from "react";
import { ViewerProps } from "../../viewer-context/withViewer";
import { FormatOptions } from "./FormatOptions";
import useJigsawEvent from "../../viewer-context/useJigsawEvent";
import JigsawEvent from "../../viewer-context/JigsawEvent";
import useOptionStorage from "./useOptionStorage";

export interface FormattingStateProps {
  [FormatOptions.FONT]: number | string;
  [FormatOptions.TEXT]: number | string;
  [FormatOptions.LINEHEIGHT]: number | string;
  [FormatOptions.MARGIN]: number | string;
  [FormatOptions.MODE]: number | string;
}

export interface FormattingProps {
  setTextSize: (size: number) => void;
  setDisplayMode: (size: number | string) => void;
  setPageMargin: (size: number | string) => void;
  setLineHeight: (size: number | string) => void;
  setFontFamily: (size: number | string) => void;
  removeEnhancedFormattingCSS: () => void;
  addEnhancedFormattingCSS: () => void;
  setReaderOptions: (k: FormatOptions, v: number | string | boolean) => void;
  resetReader: () => void;
  readerOptions: FormattingStateProps;
}

type FormatValue =
  | jigsaw.formatting.Font
  | jigsaw.formatting.Mode
  | jigsaw.formatting.Margin
  | jigsaw.formatting.LineHeight
  | number
  | boolean;

export default function withFormatting<P extends FormattingProps & ViewerProps>(
  Component: ComponentType<P>
): ComponentType<Omit<P, keyof FormattingProps>> {
  return function ComponentWithFormatting(props) {
    const {
      readerOptions,
      setReaderOptions,
      formattingOn,
      setFormatting,
      resetOptions,
    } = useOptionStorage();

    const viewer = props.viewer;
    const book = viewer.Book;

    const applySettings = useCallback(() => {
      if (formattingOn) {
        book.addEnhancedFormattingCSS();
        Object.values(FormatOptions).forEach(optionName => {
          const { enabled, value } = readerOptions[optionName];
          if (enabled) {
            switch (optionName) {
              case FormatOptions.FONT:
                book.setFontFamily(value as jigsaw.formatting.Font);
                break;
              case FormatOptions.TEXT:
                book.setTextSize(value as number);
                break;
              case FormatOptions.LINEHEIGHT:
                book.setLineHeight(value as jigsaw.formatting.LineHeight);
                break;
              case FormatOptions.MARGIN:
                book.setPageMargin(value as jigsaw.formatting.Margin);
                break;
              case FormatOptions.MODE:
                book.setDisplayMode(value as jigsaw.formatting.Mode);
                break;
              default:
            }
          }
        });
      }
    }, [book, readerOptions, formattingOn]);

    useJigsawEvent(JigsawEvent.PAGE_READY, applySettings);

    const toggleCSSFormatting = (k: FormatOptions, v: FormatValue) => {
      if (!formattingOn) {
        setFormatting(true);
      }

      setReaderOptions((prevState: any) => {
        return { ...prevState, [k]: { enabled: true, value: v } };
      });
    };

    useEffect(() => {
      applySettings();
    }, [applySettings]);

    const resetReader = () => {
      book.setTextSize(1);
      book.setDisplayMode("");
      book.setPageMargin("inherit");
      book.setLineHeight("");
      book.setFontFamily("default");
      book.removeEnhancedFormattingCSS();
      resetOptions();
    };

    function getAllValues() {
      return Object.values(FormatOptions).reduce(
        (acc, optionName) => {
          acc[optionName] = readerOptions[optionName].value;
          return acc;
        },
        {} as Record<FormatOptions, FormatValue>
      );
    }

    return (
      <Component
        {...(props as P)}
        resetReader={() => resetReader()}
        setReaderOptions={(k: FormatOptions, v: FormatValue) => {
          toggleCSSFormatting(k, v);
        }}
        readerOptions={getAllValues()}
      />
    );
  };
}
